import { useEffect, useState } from "react";
import axios from "axios";
import L from "leaflet";
import { Link, useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import { API } from "../App";
import { ArrowLeft, MapPin, Globe, Mail, Clock, ShieldCheck } from "lucide-react";

const CATEGORY_LABEL = {
  rete_olistica: "Rete Olistica",
  ecovillaggio: "Ecovillaggio",
  permacultura: "Permacultura",
  collettivo: "Collettivo",
  progetto: "Progetto",
  comunita_vicinato: "Comunità di Vicinato",
  associazione: "Associazione",
  cooperativa: "Cooperativa",
  rete_cooperativa: "Rete Cooperativa",
};

const COLOR = {
  rete_olistica: "#C05746",
  ecovillaggio: "#2D4A22",
  permacultura: "#79845B",
  collettivo: "#8b5a3c",
  progetto: "#525B54",
  comunita_vicinato: "#a85b3a",
  associazione: "#6b6f3f",
  cooperativa: "#3f6b6f",
  rete_cooperativa: "#5a4a8b",
};

const makeIcon = (color) => L.divIcon({
  className: "tc-marker",
  html: `<div style="width:28px;height:28px;border-radius:50%;background:${color};border:4px solid #F9F6F0;box-shadow:0 4px 12px rgba(0,0,0,0.3)"></div>`,
  iconSize: [28, 28], iconAnchor: [14, 14],
});

export default function Reality() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/realities/${id}`)
      .then(res => setR(res.data))
      .catch(() => setError(true));
  }, [id]);

  if (error) return (
    <div className="py-32 text-center" data-testid="reality-not-found">
      <p className="font-serif text-3xl text-tc-primary italic">Realtà non trovata</p>
      <Link to="/realta" className="btn-outline mt-6 inline-block">Torna alla mappa</Link>
    </div>
  );
  if (!r) return <div className="py-32 text-center text-tc-muted italic font-serif text-xl">Carico…</div>;

  const cat = CATEGORY_LABEL[r.category] || r.category;
  const color = COLOR[r.category] || "#2D4A22";

  return (
    <main className="max-w-5xl mx-auto px-6 md:px-12 py-16" data-testid="reality-detail-page">
      <Link to="/realta" className="inline-flex items-center gap-2 text-sm text-tc-muted hover:text-tc-primary mb-8" data-testid="back-realta">
        <ArrowLeft className="w-4 h-4" /> Torna alla mappa
      </Link>

      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest mb-4">
            <span className="w-2 h-2 rounded-full" style={{ background: color }} />
            <span style={{ color }}>{cat}</span>
            {r.status === "pending" && (
              <span className="inline-flex items-center gap-1 text-tc-secondary"><Clock className="w-3 h-3" /> In attesa</span>
            )}
          </div>

          <h1 className="font-serif text-5xl md:text-6xl text-tc-primary leading-[1.05] mb-6">{r.name}</h1>

          <div className="flex items-center gap-2 text-sm text-tc-muted mb-8">
            <MapPin className="w-4 h-4" /> {r.address ? `${r.address}, ` : ""}{r.city} · {r.region}
          </div>

          <p className="text-lg text-tc-text leading-relaxed whitespace-pre-line">{r.description}</p>

          {r.claimed_by_name && (
            <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-tc-accent/10 text-tc-accent text-xs">
              <ShieldCheck className="w-3.5 h-3.5" /> Profilo gestito da {r.claimed_by_name}
            </div>
          )}

          <div className="mt-10 space-y-3">
            {r.website && (
              <a href={r.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-tc-primary hover:underline" data-testid="reality-website">
                <Globe className="w-4 h-4" /> {r.website}
              </a>
            )}
            {r.contact && (
              <a href={`mailto:${r.contact}`} className="flex items-center gap-2 text-tc-primary hover:underline" data-testid="reality-contact">
                <Mail className="w-4 h-4" /> {r.contact}
              </a>
            )}
          </div>
        </div>

        <div className="md:col-span-5">
          <div className="h-[400px] rounded-3xl overflow-hidden border border-tc shadow-sm" data-testid="reality-mini-map">
            <MapContainer center={[r.lat, r.lng]} zoom={11} style={{ height: "100%", width: "100%" }}>
              <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Marker position={[r.lat, r.lng]} icon={makeIcon(color)} />
            </MapContainer>
          </div>

          <div className="mt-6 bg-tc-surface rounded-2xl p-5 border border-tc/40 text-sm text-tc-muted">
            <div className="text-[10px] uppercase tracking-widest text-tc-accent mb-2">Coordinate</div>
            <div className="font-mono text-xs">{r.lat?.toFixed(5)}, {r.lng?.toFixed(5)}</div>
            {r.created_by_name && (
              <>
                <div className="text-[10px] uppercase tracking-widest text-tc-accent mt-4 mb-2">Inserita da</div>
                <div>{r.created_by_name}</div>
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
